/**
 * Keeps the dots of an "Image tiles" section in sync with its swipeable strip,
 * and lets a mouse drag the strip when it is wider than the screen.
 * The strip itself is plain CSS scroll snapping, so it works without this module.
 */
class MediaTiles extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {number} */
  #frame = 0;

  /** True once the row has copies of its tiles on both sides and wraps around. */
  #looping = false;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const track = this.track;
    if (!track) return;

    // Theme editor: bring the selected tile into view.
    this.addEventListener(
      'shopify:block:select',
      (event) => {
        if (event.target instanceof HTMLElement) this.#scrollTo(event.target, 'auto');
      },
      { signal }
    );

    this.#setupLoop(track, signal);
    this.#bindDrag(track, signal);
    this.#bindArrows(track, signal);

    const dots = this.dots;
    if (dots.length === 0) return;

    track.addEventListener('scroll', () => this.#scheduleUpdate(), { passive: true, signal });
    window.addEventListener('resize', () => this.#scheduleUpdate(), { passive: true, signal });

    for (const [index, dot] of dots.entries()) {
      dot.addEventListener('click', () => this.goTo(index), { signal });
    }

    this.#update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
  }

  /**
   * Endless row (desktop and phones): one copy of the tiles is added before and after the real
   * ones. Whenever the row comes to rest in a copy, it jumps by exactly one set of
   * tiles, which looks the same, so it never reaches an end or shows a gap.
   * @param {HTMLElement} track
   * @param {AbortSignal} signal
   */
  #setupLoop(track, signal) {
    // Every visit starts on the first tile, even if the browser remembers an old position.
    let touched = false;
    const markTouched = () => {
      touched = true;
    };
    for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
      track.addEventListener(type, markTouched, { passive: true, signal });
    }
    this.querySelectorAll('[data-previous], [data-next], [data-dot]').forEach((control) =>
      control.addEventListener('click', markTouched, { signal })
    );

    /** @type {() => number} */
    let startPosition = () => 0;
    const goToStart = () => {
      if (!touched) track.scrollLeft = startPosition();
    };

    if (this.hasAttribute('data-loop') && !this.#looping) {
      const originals = /** @type {HTMLElement[]} */ (Array.from(track.children));
      if (originals.length >= 2 && track.scrollWidth - track.clientWidth > 1) {
        const makeCopy = (/** @type {HTMLElement} */ item) => {
          const copy = /** @type {HTMLElement} */ (item.cloneNode(true));
          copy.setAttribute('data-clone', '');
          copy.setAttribute('aria-hidden', 'true');
          // Copies stay clickable (they are on screen) but are skipped by the keyboard.
          copy.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) =>
            node.setAttribute('tabindex', '-1')
          );
          for (const attribute of Array.from(copy.attributes)) {
            if (attribute.name.startsWith('data-shopify')) copy.removeAttribute(attribute.name);
          }
          copy.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
          return copy;
        };

        track.prepend(...originals.map(makeCopy));
        track.append(...originals.map(makeCopy));
        this.#looping = true;
        this.setAttribute('data-looping', '');

        const firstItem = /** @type {HTMLElement} */ (track.firstElementChild);
        const setWidth = () => originals[0].offsetLeft - firstItem.offsetLeft;
        // Start with the first real tile at the row's resting edge (for product rows: in
        // line with the logo, with copies filling the space to the left of it).
        startPosition = () => setWidth() - this.#startInset(track);

        // A finger still on the screen means the swipe is not over yet.
        let fingerDown = false;
        track.addEventListener('touchstart', () => (fingerDown = true), { passive: true, signal });
        track.addEventListener('touchend', () => (fingerDown = false), { passive: true, signal });
        track.addEventListener('touchcancel', () => (fingerDown = false), { passive: true, signal });

        // When the row rests in a copy, jump by one full set: it looks identical.
        const settle = () => {
          if (fingerDown || track.hasAttribute('data-dragging')) return;
          const width = setWidth();
          if (width <= 0) return;
          if (track.scrollLeft < width * 0.5) {
            track.scrollLeft += width;
          } else if (track.scrollLeft >= width * 1.5) {
            track.scrollLeft -= width;
          }
        };

        let timer = 0;
        track.addEventListener(
          'scroll',
          () => {
            clearTimeout(timer);
            timer = window.setTimeout(settle, 160);
          },
          { passive: true, signal }
        );
        if ('onscrollend' in window) track.addEventListener('scrollend', settle, { signal });
        window.addEventListener('resize', settle, { passive: true, signal });
      }
    }

    goToStart();
    requestAnimationFrame(goToStart);
    window.addEventListener('load', goToStart, { signal });
    window.addEventListener('pageshow', goToStart, { signal });
  }

  /**
   * Mouse drag to move the row sideways, only while the row is wider than the screen.
   * A drag of more than a few pixels does not count as a click on a tile.
   * @param {HTMLElement} track
   * @param {AbortSignal} signal
   */
  #bindDrag(track, signal) {
    let pointerId = -1;
    let startX = 0;
    let startScroll = 0;
    let moved = false;

    const canDrag = () => track.scrollWidth - track.clientWidth > 1;
    const markDraggable = () => track.toggleAttribute('data-draggable', canDrag());
    markDraggable();
    window.addEventListener('resize', markDraggable, { passive: true, signal });

    track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    // Photo carousels inside product cards should not start their own mouse drag.
    track.addEventListener(
      'mousedown',
      (event) => {
        if (event.button === 0 && canDrag()) event.stopPropagation();
      },
      { capture: true, signal }
    );

    track.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || !canDrag()) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = track.scrollLeft;
        moved = false;
      },
      { signal }
    );

    window.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerId !== pointerId) return;
        const distance = event.clientX - startX;
        if (!moved) {
          if (Math.abs(distance) < 6) return;
          moved = true;
          track.setAttribute('data-dragging', '');
        }
        event.preventDefault();
        track.scrollLeft = startScroll - distance;
      },
      { signal }
    );

    /** @param {PointerEvent} event */
    const stop = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = -1;
      if (!moved) return;
      track.removeAttribute('data-dragging');
      // Let the row settle on the nearest tile, then forget the drag after the click.
      const items = /** @type {HTMLElement[]} */ (Array.from(track.children));
      const inset = this.#startInset(track);
      let nearest = items[0];
      let best = Number.POSITIVE_INFINITY;
      for (const item of items) {
        const gap = Math.abs(item.offsetLeft - inset - track.scrollLeft);
        if (gap < best) {
          best = gap;
          nearest = item;
        }
      }
      if (nearest) this.#scrollTo(nearest, 'smooth');
      setTimeout(() => {
        moved = false;
      }, 0);
    };

    window.addEventListener('pointerup', stop, { signal });
    window.addEventListener('pointercancel', stop, { signal });

    track.addEventListener(
      'click',
      (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
      },
      { capture: true, signal }
    );
  }

  /**
   * Previous/next buttons: move by the number of whole tiles on screen, and hide
   * when the row fits on the screen.
   * @param {HTMLElement} track
   * @param {AbortSignal} signal
   */
  #bindArrows(track, signal) {
    const previous = this.querySelector('[data-previous]');
    const next = this.querySelector('[data-next]');
    if (!(previous instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;

    const update = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      const scrollable = maxScroll > 1;
      previous.hidden = !scrollable;
      next.hidden = !scrollable;
      const position = Math.abs(track.scrollLeft);
      previous.disabled = !this.#looping && position <= 1;
      next.disabled = !this.#looping && position >= maxScroll - 1;
    };

    const move = (/** @type {1 | -1} */ direction) => {
      const item = this.items[0];
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = item ? item.offsetWidth + gap : track.clientWidth;
      const perView = Math.max(1, Math.floor((track.clientWidth + gap) / step));
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollBy({ left: direction * step * perView, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    previous.addEventListener('click', () => move(-1), { signal });
    next.addEventListener('click', () => move(1), { signal });

    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    track.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    update();
  }

  /**
   * Where a tile rests, measured from the row's left edge: the scroll padding when set,
   * otherwise the padding.
   * @param {HTMLElement} track
   */
  #startInset(track) {
    const style = getComputedStyle(track);
    return parseFloat(style.scrollPaddingInlineStart) || parseFloat(style.paddingInlineStart) || 0;
  }

  /** @returns {HTMLElement | null} */
  get track() {
    return this.querySelector('[data-track]');
  }

  /** @returns {HTMLElement[]} */
  get dots() {
    return Array.from(this.querySelectorAll('[data-dot]'));
  }

  /** @returns {HTMLElement[]} */
  get items() {
    const track = this.track;
    if (!track) return [];
    return /** @type {HTMLElement[]} */ (Array.from(track.children)).filter((item) => !item.hasAttribute('data-clone'));
  }

  /**
   * Scrolls the strip to the tile at the given index.
   * @param {number} index
   */
  goTo(index) {
    const item = this.items[index];
    if (!item) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#scrollTo(item, reduceMotion ? 'auto' : 'smooth');
  }

  /**
   * @param {HTMLElement} item
   * @param {ScrollBehavior} behavior
   */
  #scrollTo(item, behavior) {
    const track = this.track;
    if (!track) return;

    const offset = item.getBoundingClientRect().left - track.getBoundingClientRect().left;
    const inset = this.#startInset(track);

    track.scrollTo({ left: track.scrollLeft + offset - inset, behavior });
  }

  #scheduleUpdate() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const track = this.track;
    const items = this.items;
    if (!track || items.length === 0) return;

    const trackLeft = track.getBoundingClientRect().left;
    const inset = this.#startInset(track);

    let activeIndex = 0;
    let smallestDistance = Number.POSITIVE_INFINITY;

    for (const [index, item] of items.entries()) {
      const distance = Math.abs(item.getBoundingClientRect().left - trackLeft - inset);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        activeIndex = index;
      }
    }

    for (const [index, dot] of this.dots.entries()) {
      if (index === activeIndex) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('media-tiles')) {
  customElements.define('media-tiles', MediaTiles);
}
